import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dropdown,
  type DropdownItem,
  Input,
  Textarea,
  useToast,
} from "@/components/ui";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ChevronDown } from "lucide-react";
import { providerLabel, useVoiceOptions } from "./useVoiceOptions";
import {
  callingAgentsService,
  type CallingAgentPrerequisites,
  type CreateCallingAgentRequest,
} from "@/services/api/callingAgents";

interface Props {
  afterSubmit: () => void;
}

const AGENT_TYPES = ["outbound", "inbound"];
const FALLBACK_PREREQUISITES: CallingAgentPrerequisites = {
  voice_providers: ["vapi", "retell", "twilio"],
  languages: ["en", "hi", "multi"],
};

const LANGUAGE_LABELS: Record<string, string> = {
  en: "English",
  hi: "Hindi",
  multi: "Multilingual",
};


const labelize = (value: string) =>
  LANGUAGE_LABELS[value] ??
  value
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const CreateAgentForm: React.FC<Props> = ({ afterSubmit }) => {
  const { success, error: toastError } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prerequisites, setPrerequisites] = useState<CallingAgentPrerequisites>(
    FALLBACK_PREREQUISITES
  );

  useEffect(() => {
    callingAgentsService
      .prerequisites()
      .then((res) => {
        const attributes = res?.attributes;
        if (
          attributes?.voice_providers?.length ||
          attributes?.languages?.length
        ) {
          setPrerequisites({
            voice_providers:
              attributes.voice_providers ??
              FALLBACK_PREREQUISITES.voice_providers,
            languages: attributes.languages ?? FALLBACK_PREREQUISITES.languages,
          });
        }
      })
      .catch(() => {});
  }, []);

  const voiceProviders = useMemo(
    () =>
      prerequisites.voice_providers.length
        ? prerequisites.voice_providers
        : FALLBACK_PREREQUISITES.voice_providers,
    [prerequisites.voice_providers]
  );

  const languages = useMemo(
    () =>
      prerequisites.languages.length
        ? prerequisites.languages
        : FALLBACK_PREREQUISITES.languages,
    [prerequisites.languages]
  );

  const formik = useFormik({
    initialValues: {
      type: "outbound",
      display_name: "",
      description: "",
      voice_provider: "vapi",
      voice_id: "",
      language: "",
      first_message: "",
      prompt: "",
    },
    validationSchema: Yup.object({
      type: Yup.string().required("Agent type is required"),
      display_name: Yup.string().required("Display name is required"),
      voice_provider: Yup.string().required("Voice provider is required"),
      voice_id: Yup.string().required("Voice is required"),
      language: Yup.string().required("Language is required"),
      first_message: Yup.string().required("First message is required"),
      prompt: Yup.string().required("Prompt is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      try {
        const payload: CreateCallingAgentRequest = {
          type: values.type,
          display_name: values.display_name,
          description: values.description,
          voice_provider: values.voice_provider,
          voice_id: values.voice_id,
          language: values.language,
          prompt: values.prompt,
          settings: {
            first_message: values.first_message,
          },
          data_details: {},
        };

        const res: any = await callingAgentsService.create(payload);
        success(res?.message || "Calling agent created successfully");
        resetForm();
        afterSubmit();
      } catch (e: any) {
        toastError(e?.message || "Failed to create agent");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const voiceOptions = useVoiceOptions({
    provider: formik.values.voice_provider,
    language: formik.values.language,
    voiceId: formik.values.voice_id,
    onSelect: (id) => formik.setFieldValue("voice_id", id),
  });

  // Changing provider/language invalidates the chosen voice
  const setAndClearVoice = (field: string) => (v: string) => {
    formik.setFieldValue(field, v);
    formik.setFieldValue("voice_id", "");
  };

  const dropdownTrigger = (display: string, placeholder = "Select") => (
    <div className="h-11 bg-white border border-gray-300 rounded-md px-3 py-2 text-gray-700 flex justify-between items-center cursor-pointer w-full text-xs">
      <span className={display ? "text-gray-900" : "text-gray-500"}>
        {display || placeholder}
      </span>
      <ChevronDown size={16} className="text-gray-400" />
    </div>
  );

  const makeItems = (
    values: string[],
    setter: (v: string) => void,
    formatter: (v: string) => string = labelize
  ): DropdownItem[] =>
    values.map((v) => ({
      label: formatter(v),
      onClick: () => setter(v),
    }));

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs">
            Display Name <span className="text-red-500">*</span>
          </label>
          <Input
            name="display_name"
            value={formik.values.display_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="h-11"
          />
          {formik.touched.display_name && formik.errors.display_name && (
            <p className="text-red-500 text-sm mt-1">
              {formik.errors.display_name}
            </p>
          )}
        </div>

        <div>
          <label className="text-xs">
            Agent Type <span className="text-red-500">*</span>
          </label>
          <Dropdown
            trigger={dropdownTrigger(labelize(formik.values.type))}
            items={makeItems(AGENT_TYPES, (v) =>
              formik.setFieldValue("type", v)
            )}
            align="left"
            triggerClassName="py-0"
          />
          {formik.touched.type && formik.errors.type && (
            <p className="text-red-500 text-sm mt-1">{formik.errors.type}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs">
            Voice Provider <span className="text-red-500">*</span>
          </label>
          <Dropdown
            trigger={dropdownTrigger(
              formik.values.voice_provider
                ? providerLabel(formik.values.voice_provider)
                : ""
            )}
            items={makeItems(
              voiceProviders,
              setAndClearVoice("voice_provider"),
              providerLabel
            )}
            align="left"
            triggerClassName="py-0"
          />
          {formik.touched.voice_provider && formik.errors.voice_provider && (
            <p className="text-red-500 text-sm mt-1">
              {formik.errors.voice_provider}
            </p>
          )}
        </div>

        <div>
          <label className="text-xs">
            Language <span className="text-red-500">*</span>
          </label>
          <Dropdown
            trigger={dropdownTrigger(labelize(formik.values.language))}
            items={makeItems(languages, setAndClearVoice("language"))}
            align="left"
            triggerClassName="py-0"
          />
          {formik.touched.language && formik.errors.language && (
            <p className="text-red-500 text-sm mt-1">
              {formik.errors.language}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Voice dropdown — replaces the free-text Voice ID input */}
        <div>
          <label className="text-xs">
            Voice <span className="text-red-500">*</span>
          </label>
          <Dropdown
            trigger={dropdownTrigger(voiceOptions.selectedLabel, voiceOptions.placeholder)}
            items={voiceOptions.items}
            header={voiceOptions.header}
            align="left"
            triggerClassName="py-0"
          />
          {formik.touched.voice_id && formik.errors.voice_id && (
            <p className="text-red-500 text-sm mt-1">
              {formik.errors.voice_id}
            </p>
          )}
        </div>

        <div>
          <label className="text-xs">
            First Message <span className="text-red-500">*</span>
          </label>
          <Input
            name="first_message"
            value={formik.values.first_message}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="h-11"
          />
          {formik.touched.first_message && formik.errors.first_message && (
            <p className="text-red-500 text-sm mt-1">
              {formik.errors.first_message}
            </p>
          )}
        </div>
      </div>

      <div>
        <label className="text-xs">Description</label>
        <Input
          name="description"
          value={formik.values.description}
          onChange={formik.handleChange}
          className="h-11"
        />
      </div>

      <div>
        <label className="text-xs">
          Prompt <span className="text-red-500">*</span>
        </label>
        <Textarea
          name="prompt"
          value={formik.values.prompt}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          rows={10}
          className="text-sm"
        />
        {formik.touched.prompt && formik.errors.prompt && (
          <p className="text-red-500 text-sm mt-1">{formik.errors.prompt}</p>
        )}
      </div>

      <div className="pt-4 flex justify-end">
        <Button
          type="submit"
          disabled={isSubmitting}
          className="bg-primary-button hover:bg-primary-button text-white font-bold py-2 px-4 rounded-md"
        >
          {isSubmitting ? "Creating..." : "Submit & Create Agent"}
        </Button>
      </div>
    </form>
  );
};

export default CreateAgentForm;
